import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Layers, Scale, Wand2, FileDown, CheckCircle2, AlertTriangle } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { exportToCsv } from '@/lib/exportCsv';

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─────────────────────── (a) Smart Combination Match ───────────────────────
function SmartMatchTab() {
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null); // row being worked
  const [sugg, setSugg] = useState(null);
  const [tolAbs, setTolAbs] = useState(2);

  const loadUnmatched = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/recon-cockpit/unmatched?kind=debit&limit=80');
      setUnmatched(data.rows || []);
    } catch { toast.error('Failed to load unmatched lines'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadUnmatched(); }, []);

  const openSuggestions = async (row) => {
    setActive(row); setSugg(null);
    try {
      const { data } = await api.get(`/recon-enhance/combo-suggestions?statement_id=${row.statement_id}&txn_index=${row.txn_index}&tolerance_abs=${tolAbs}&tolerance_pct=0.5&window_days=45`);
      setSugg(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to fetch suggestions'); }
  };

  const applyCombo = async (components) => {
    if (!active) return;
    try {
      await api.post('/recon-enhance/combo-match', {
        statement_id: active.statement_id, txn_index: active.txn_index,
        components: components.map(c => ({ type: c.type, id: c.id })),
      });
      toast.success(`Reconciled against ${components.length} record(s)`);
      setActive(null); setSugg(null);
      loadUnmatched();
    } catch (e) { toast.error(e.response?.data?.detail || 'Match failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">Reconcile a single bank line against <strong>several</strong> invoices/expenses that sum to it — with small fee/rounding tolerance.</p>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Tolerance ±</Label>
          <Input type="number" value={tolAbs} onChange={e => setTolAbs(e.target.value)} className="w-20 h-8" data-testid="smart-tol-abs" />
        </div>
        <Button size="sm" variant="outline" onClick={loadUnmatched} data-testid="smart-refresh">Refresh</Button>
      </div>

      {loading ? <div className="py-10 text-center text-muted-foreground">Loading…</div> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="smart-unmatched-table">
                <thead><tr className="border-b bg-muted/50 text-muted-foreground text-xs">
                  <th className="text-left p-2.5">Date</th><th className="text-left p-2.5">Description</th>
                  <th className="text-right p-2.5">Amount</th><th className="text-center p-2.5">Action</th>
                </tr></thead>
                <tbody>
                  {unmatched.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No unmatched outgoing lines 🎉</td></tr>}
                  {unmatched.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-muted/40" data-testid={`smart-row-${i}`}>
                      <td className="p-2.5 text-xs">{r.date}</td>
                      <td className="p-2.5 text-xs max-w-md truncate">{r.description}</td>
                      <td className="p-2.5 text-right font-mono"><RiyalIcon size="0.7em" /> {money(r.amount)}</td>
                      <td className="p-2.5 text-center">
                        <Button size="sm" variant="outline" onClick={() => openSuggestions(r)} data-testid={`smart-find-${i}`}>
                          <Layers size={13} className="mr-1" /> Find combo
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {active && (
        <Card className="border-primary/40" data-testid="smart-suggestions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers size={16} /> Matches for <span className="font-mono"><RiyalIcon size="0.7em" /> {money(active.amount)}</span>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { setActive(null); setSugg(null); }}>Close</Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!sugg ? <div className="text-sm text-muted-foreground">Loading suggestions…</div> : (
              <>
                {sugg.single_matches.length === 0 && sugg.combo_matches.length === 0 && (
                  <div className="text-sm text-muted-foreground">No single or combination matches within tolerance. Try increasing tolerance or widening the date window.</div>
                )}
                {[...sugg.single_matches, ...sugg.combo_matches].map((m, i) => (
                  <div key={i} className="flex items-center gap-3 border rounded-lg p-2.5 hover:bg-muted/30" data-testid={`smart-combo-${i}`}>
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-1.5">
                        {m.components.map((c, j) => (
                          <Badge key={j} variant="outline" className="text-[10px] gap-1">{c.label?.slice(0, 24) || c.type} · {money(c.amount)}</Badge>
                        ))}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Total {money(m.total)} {m.diff > 0.001 ? <span className="text-warning">· fee/rounding Δ {money(m.diff)}</span> : <span className="text-success">· exact</span>}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => applyCombo(m.components)} data-testid={`smart-apply-${i}`}>Match</Button>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────── (b) Reconciliation Closing Report ───────────────────────
function ClosingReportTab() {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState('');
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/bank-accounts?active_only=true').then(({ data }) => { setAccounts(data || []); if (data?.[0]) setAccId(data[0].id); }).catch(() => {}); }, []);

  const run = async () => {
    if (!accId) { toast.error('Pick a bank account'); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/recon-enhance/reconciliation-report?bank_account_id=${accId}&as_of=${asOf}`);
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (accId) run(); /* eslint-disable-next-line */ }, [accId]);

  const exportCsv = () => {
    if (!report) return;
    const rows = [
      { item: 'Balance per Books', amount: report.book.balance },
      { item: 'Add: Deposits in transit (bank credits not in books)', amount: report.reconciling_items.deposits_in_transit.total },
      { item: 'Less: Outstanding payments (bank debits not in books)', amount: -report.reconciling_items.outstanding_payments.total },
      { item: 'Adjusted Book Balance', amount: report.adjusted_book_balance },
      { item: 'Balance per Bank Statement', amount: report.bank_statement_balance },
      { item: 'Residual Difference', amount: report.residual_difference },
    ];
    exportToCsv(`bank-recon-${accId}-${asOf}`, rows, [
      { key: 'item', label: 'Item' },
      { key: 'amount', label: 'Amount', format: (v) => money(v) },
    ]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">Bank Account</Label>
          <Select value={accId} onValueChange={setAccId}>
            <SelectTrigger className="w-56" data-testid="recon-acct-select"><SelectValue placeholder="Pick account" /></SelectTrigger>
            <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name || a.bank_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">As of</Label><Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="w-40" data-testid="recon-asof" /></div>
        <Button onClick={run} data-testid="recon-run">Generate</Button>
        {report && <Button variant="outline" onClick={exportCsv} data-testid="recon-export"><FileDown size={14} className="mr-1" /> Export CSV</Button>}
      </div>

      {loading ? <div className="py-10 text-center text-muted-foreground">Loading…</div> : report && (
        <Card data-testid="recon-report-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale size={16} /> {report.bank_account.name} — Reconciliation as of {report.as_of}
              {report.reconciled
                ? <Badge className="bg-success ml-2"><CheckCircle2 size={12} className="mr-1" /> Reconciled</Badge>
                : <Badge className="bg-warning ml-2"><AlertTriangle size={12} className="mr-1" /> Difference {money(report.residual_difference)}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm max-w-2xl">
                <tbody>
                  <tr className="border-b"><td className="py-2">Balance per Books</td><td className="py-2 text-right font-mono">{money(report.book.balance)}</td></tr>
                  <tr className="border-b text-success"><td className="py-2 pl-4">Add: Deposits in transit ({report.reconciling_items.deposits_in_transit.count})</td><td className="py-2 text-right font-mono">+ {money(report.reconciling_items.deposits_in_transit.total)}</td></tr>
                  <tr className="border-b text-error"><td className="py-2 pl-4">Less: Outstanding payments ({report.reconciling_items.outstanding_payments.count})</td><td className="py-2 text-right font-mono">− {money(report.reconciling_items.outstanding_payments.total)}</td></tr>
                  <tr className="border-b font-semibold"><td className="py-2">Adjusted Book Balance</td><td className="py-2 text-right font-mono">{money(report.adjusted_book_balance)}</td></tr>
                  <tr className="border-b"><td className="py-2">Balance per Bank Statement {report.bank_statement_balance_date ? `(${report.bank_statement_balance_date})` : ''}</td><td className="py-2 text-right font-mono">{report.bank_statement_balance == null ? '—' : money(report.bank_statement_balance)}</td></tr>
                  <tr className={report.reconciled ? 'text-success font-bold' : 'text-warning font-bold'}><td className="py-2">Residual Difference</td><td className="py-2 text-right font-mono">{money(report.residual_difference)}</td></tr>
                </tbody>
              </table>
            </div>

            {report.reconciling_items.outstanding_payments.count > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Outstanding payments (in bank, not in books)</p>
                <div className="border rounded-lg max-h-60 overflow-auto">
                  <table className="w-full text-xs"><tbody>
                    {report.reconciling_items.outstanding_payments.rows.map((r, i) => (
                      <tr key={i} className="border-b"><td className="p-2">{r.date}</td><td className="p-2 truncate max-w-xs">{r.description}</td><td className="p-2 text-right font-mono">{money(r.amount)}</td></tr>
                    ))}
                  </tbody></table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────── (c) Bulk Rule Categorization ───────────────────────
function BulkCategorizeTab() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ with_rule: 0, rules_active: 0 });
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  const [onlyWithRule, setOnlyWithRule] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/recon-enhance/bulk-review?limit=500');
      setRows(data.rows || []);
      setMeta({ with_rule: data.with_rule, rules_active: data.rules_active });
      const pre = {};
      (data.rows || []).forEach((r, i) => { if (r.rule) pre[i] = true; });
      setSel(pre);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = onlyWithRule ? rows.filter(r => r.rule) : rows;

  const apply = async () => {
    const chosen = rows.map((r, i) => ({ r, i })).filter(({ r, i }) => sel[i] && r.rule);
    if (chosen.length === 0) { toast.error('Select rows that have a matched rule'); return; }
    try {
      const { data } = await api.post('/recon-enhance/bulk-apply', {
        rows: chosen.map(({ r }) => ({
          statement_id: r.statement_id, txn_index: r.txn_index,
          category: r.rule.category, kind: r.rule.kind,
          vendor_name: r.rule.vendor_name, supplier_id: r.rule.supplier_id,
          branch_id: r.rule.branch_id,
        })),
      });
      toast.success(`Created ${data.created} record(s)${data.failed.length ? `, ${data.failed.length} failed` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground flex-1">{meta.rules_active} active rule(s) · <strong>{meta.with_rule}</strong> unmatched lines hit a rule. Review and create the entries in bulk.</p>
        <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={onlyWithRule} onCheckedChange={v => setOnlyWithRule(!!v)} data-testid="bulk-only-rule" /> Only rows with a rule</label>
        <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
        <Button size="sm" onClick={apply} data-testid="bulk-apply-btn"><Wand2 size={14} className="mr-1" /> Create selected</Button>
      </div>

      {loading ? <div className="py-10 text-center text-muted-foreground">Loading…</div> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bulk-review-table">
                <thead><tr className="border-b bg-muted/50 text-muted-foreground text-xs">
                  <th className="p-2.5 w-8"></th><th className="text-left p-2.5">Date</th><th className="text-left p-2.5">Description</th>
                  <th className="text-right p-2.5">Amount</th><th className="text-left p-2.5">Rule → Category</th>
                </tr></thead>
                <tbody>
                  {visible.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">{onlyWithRule ? 'No unmatched lines matched a rule. Create rules in Auto-Rules first.' : 'No unmatched lines.'}</td></tr>}
                  {visible.map((r) => {
                    const realIdx = rows.indexOf(r);
                    return (
                      <tr key={realIdx} className="border-b hover:bg-muted/40" data-testid={`bulk-row-${realIdx}`}>
                        <td className="p-2.5 text-center"><Checkbox checked={!!sel[realIdx]} disabled={!r.rule} onCheckedChange={v => setSel(s => ({ ...s, [realIdx]: !!v }))} data-testid={`bulk-check-${realIdx}`} /></td>
                        <td className="p-2.5 text-xs">{r.date}</td>
                        <td className="p-2.5 text-xs max-w-md truncate">{r.description}</td>
                        <td className="p-2.5 text-right font-mono"><RiyalIcon size="0.7em" /> {money(r.amount)}</td>
                        <td className="p-2.5 text-xs">{r.rule ? <Badge variant="outline" className="text-[10px]">{r.rule.name} → {r.rule.category || r.rule.kind}</Badge> : <span className="text-muted-foreground">no rule</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function BankReconEnhancePage() {
  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
            <Scale size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Smart Reconciliation</h1>
            <p className="text-sm text-muted-foreground">Combination matching · bank-vs-book closing report · bulk rule categorization</p>
          </div>
        </div>

        <Tabs defaultValue="smart">
          <TabsList data-testid="recon-enhance-tabs">
            <TabsTrigger value="smart" data-testid="tab-smart"><Layers size={14} className="mr-1.5" /> Smart Match</TabsTrigger>
            <TabsTrigger value="closing" data-testid="tab-closing"><Scale size={14} className="mr-1.5" /> Closing Report</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk"><Wand2 size={14} className="mr-1.5" /> Bulk Categorize</TabsTrigger>
          </TabsList>
          <TabsContent value="smart" className="mt-4"><SmartMatchTab /></TabsContent>
          <TabsContent value="closing" className="mt-4"><ClosingReportTab /></TabsContent>
          <TabsContent value="bulk" className="mt-4"><BulkCategorizeTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
